import Link from 'next/link';
import { FC } from 'react';
import { FiInstagram } from 'react-icons/fi';
import { TfiFacebook } from 'react-icons/tfi';
import styled from 'styled-components';
import { useStore } from '../../lib/store';
import { breakpoints } from '../../styles/breakpoints';
import { globalTheme } from '../../styles/global-theme';
import { Icon } from '../ui';

const Wrapper = styled.div`
  background-color: ${globalTheme.colors.darkBlue};
  padding: ${globalTheme.space[2]} ${globalTheme.space[4]};
`;
const Row = styled.div`
  display: flex;
  align-items: center;

  @media screen and (max-width: ${breakpoints.medium}) {
    flex-direction: column;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: row;
  margin-left: ${globalTheme.space[2]};

  @media screen and (max-width: ${breakpoints.medium}) {
    flex-direction: column;
    align-items: center;
    margin: ${globalTheme.space[2]} 0;
  }

  & > a {
    color: ${globalTheme.colors.white};
    padding-right: ${globalTheme.space[4]};
    text-decoration: none;

    @media screen and (max-width: ${breakpoints.medium}) {
      padding-right: 0;
    }
  }
`;

const Socials = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-grow: 1;

  @media screen and (max-width: ${breakpoints.medium}) {
    justify-content: center;
    flex-grow: inherit;
  }

  > * {
    margin-right: ${globalTheme.space[1]};

    @media screen and (max-width: ${breakpoints.medium}) {
      margin: 0 ${globalTheme.space[0]};
    }
  }
`;

const Contact = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${breakpoints.small}) {
    align-items: center;
  }

  & > a {
    font-size: ${globalTheme.fontSizes[0]};
    color: ${globalTheme.colors.background};
  }
`;

const BottomRow = styled(Row)`
  margin-top: ${globalTheme.space[8]};

  @media screen and (max-width: ${breakpoints.large}) {
    flex-direction: row;
  }

  @media screen and (max-width: ${breakpoints.small}) {
    flex-direction: column;
    margin-top: ${globalTheme.space[4]};
  }
`;

const Legal = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
  align-items: center;

  & > a {
    color: ${globalTheme.colors.white};
    font-size: ${globalTheme.fontSizes[0]};
  }

  & > * {
    color: ${globalTheme.colors.white};
  }
`;

const StyledPipe = styled.span`
  padding: 0 ${globalTheme.space[0]};
`;

type NavItem = {
  title: string;
  href: string;
  externalLink: boolean;
};

let footerNavItems = [
  {
    title: 'For buyers',
    href: 'https://agoralivestock.com.au/for-buyers/',
    externalLink: true,
  },
  {
    title: 'For sellers',
    href: 'https://agoralivestock.com.au/for-sellers/',
    externalLink: true,
  },
  {
    title: 'About us',
    href: 'https://agoralivestock.com.au/about-us/',
    externalLink: true,
  },
  {
    title: 'FAQS',
    href: 'https://agoralivestock.com.au/faqs/',
    externalLink: true,
  },
  {
    title: 'Contact',
    href: 'https://agoralivestock.com.au/contact/',
    externalLink: true,
  },
  {
    title: 'Login',
    href: '/auth/login',
    externalLink: false,
  },
];

const MarketplaceFooter: FC = () => {
  const userSession = useStore((store) => store.accessToken);

  const filteredNavItems = userSession
    ? footerNavItems.filter((item: NavItem) => {
        return item.title !== 'Login';
      })
    : footerNavItems;

  const nav = (navItems: NavItem[]) => {
    return navItems.map((item) => {
      return (
        <Link
          key={item.title}
          href={item.href}
          title={item.title}
          legacyBehavior
        >
          {!item.externalLink ? (
            item.title
          ) : (
            <a target='_blank' rel='noopener'>
              {item.title}
            </a>
          )}
        </Link>
      );
    });
  };

  return (
    <Wrapper>
      <Row>
        <Icon component='AgoraLogo' size={3} dontFill />
        <Nav>{nav(filteredNavItems)}</Nav>
        <Socials>
          <Link
            href={'https://www.instagram.com/agoralivestock/?hl=en'}
            target='_blank'
          >
            <FiInstagram color={'white'} />
          </Link>
          <Link
            href={'https://www.facebook.com/AgoraLivestock/'}
            target={'_blank'}
          >
            <TfiFacebook color={'white'} />
          </Link>
        </Socials>
      </Row>
      <BottomRow>
        <Contact>
          <Link href={'tel:1300 812 345'}>P: 1300 812 345</Link>
          <Link href={'mailto:info@agoralivestock.com.au'}>
            E: info@agoralivestock.com.au
          </Link>
        </Contact>
        <Legal>
          <Link
            href={'https://agoralivestock.com.au/privacy-policy/'}
            target='_blank'
          >
            Privacy policy
          </Link>
        </Legal>
      </BottomRow>
    </Wrapper>
  );
};

export default MarketplaceFooter;
